import type { Program } from "./types";
import { assets, type ProgramSlug } from "../assets";

function p(
  slug: ProgramSlug,
  title: string,
  shortDescription: string,
  features: string[],
  hero: Program["hero"],
  overview: Program["overview"],
  challenges: Program["challenges"],
  strategy: Program["strategy"],
  apply?: Program["apply"]
): Program {
  const image = assets.programs[slug];
  return {
    slug,
    title,
    shortDescription,
    features,
    image,
    hero,
    overview,
    challenges,
    strategy,
    ...(apply ? { apply } : {}),
    seo: {
      title: `${title} | AJA Abhaya Hastham Foundation`,
      description: shortDescription,
      ogImage: image,
    },
  };
}

export const programs: Program[] = [
  p(
    "healthcare",
    "Healthcare",
    "Mobile health camps, preventive care, and medical support for underserved communities across Telangana.",
    ["Medical Camp", "Community Health", "Blood Donation Camp"],
    {
      headline: "Compassionate",
      accent: "Healthcare",
      subtext:
        "Bringing doctors, medicines, and preventive care to villages and communities that need it most.",
    },
    {
      title: "Healthcare Overview",
      icon: "🏥",
      description:
        "AJA Abhaya Hastham runs community health camps, medicine distribution drives, and blood donation initiatives across Hyderabad and rural Telangana. Our volunteers and medical partners focus on accessible, dignified care — meeting people where they are, with compassion at the centre of every outreach.",
      stats: [
        { value: "50+", label: "Health camps held" },
        { value: "12,000+", label: "Beneficiaries served" },
        { value: "15+", label: "Villages reached" },
      ],
      features: [
        "Mobile medical camps",
        "Community health screenings",
        "Referrals to blood donation camps",
        "Medicine distribution",
        "Health awareness sessions",
      ],
    },
    {
      title: "Challenges we address",
      icon: "⚡",
      items: [
        { title: "Limited rural access", desc: "Many families travel long distances or go without care when clinics are far away." },
        { title: "Rising medical costs", desc: "Out-of-pocket expenses push vulnerable households deeper into hardship." },
        { title: "Workforce gaps", desc: "Remote areas lack enough doctors, nurses, and trained health workers." },
        { title: "Preventive care gaps", desc: "Early screening and awareness are often missing in underserved communities." },
      ],
    },
    {
      title: "Our approach",
      icon: "🎯",
      items: [
        { number: "01", title: "Mobile health camps", desc: "Regular camps with doctors, nurses, and basic diagnostics in community centres." },
        { number: "02", title: "Preventive outreach", desc: "Screenings, nutrition guidance, and awareness on hygiene and chronic conditions." },
        { number: "03", title: "Medicine support", desc: "Essential medicines and supplies for families who cannot afford treatment." },
        { number: "04", title: "Partnerships", desc: "Collaborating with hospitals, blood banks, and local NGOs for sustained impact." },
      ],
    }
  ),
  p(
    "education",
    "Education",
    "School kits, learning support, and educational outreach for children across underserved communities in Telangana.",
    ["Educational Support", "Educational Kit Distribution", "Academic Development"],
    {
      headline: "Empowering Through",
      accent: "Education",
      subtext:
        "Helping children stay in school with kits, tuition support, and community learning programmes across Hyderabad and rural Telangana.",
    },
    {
      title: "Education Overview",
      icon: "🎓",
      description:
        "AJA Abhaya Hastham supports students who lack basic learning materials and after-school help. We distribute educational kits, run community learning sessions, and work with schools and volunteers so every child has a fair chance to learn and grow.",
      stats: [
        { value: "2,000+", label: "Students supported" },
        { value: "25+", label: "Schools reached" },
        { value: "500+", label: "Kits distributed" },
      ],
      features: [
        "Educational kit distribution",
        "After-school learning support",
        "Teacher and volunteer aid",
        "Scholarship assistance",
        "Community learning sessions",
      ],
    },
    {
      title: "Challenges we address",
      icon: "⚡",
      items: [
        { title: "Cost of schooling", desc: "Books, uniforms, and fees push many families to drop children out of school." },
        { title: "Learning gaps", desc: "Children in underserved areas often fall behind without extra support at home." },
        { title: "Teacher shortages", desc: "Rural schools struggle with too few trained educators for growing classes." },
        { title: "Access barriers", desc: "Long distances and household work keep girls and boys from attending regularly." },
      ],
    },
    {
      title: "Our approach",
      icon: "🎯",
      items: [
        { number: "01", title: "Kit distribution drives", desc: "Notebooks, stationery, and essentials before each academic year." },
        { number: "02", title: "Learning centres", desc: "After-school support with volunteers and mentors in community spaces." },
        { number: "03", title: "School partnerships", desc: "Working with government and local schools to reach more students." },
        { number: "04", title: "Scholarship support", desc: "Financial help for deserving students to continue higher studies." },
      ],
    }
  ),
  p(
    "goshala",
    "Goshala",
    "Cow rescue, shelter, veterinary care, and goshala maintenance with compassion and community seva.",
    ["Animal Rescue", "Veterinary Care", "Adoption Drives", "Shelter Support"],
    {
      headline: "Protecting Life Through",
      accent: "Goshala",
      subtext:
        "Providing shelter, feed, and veterinary care for cows in need — honouring tradition while serving animals with dignity.",
    },
    {
      title: "Goshala Overview",
      icon: "🐄",
      description:
        "Our goshala programme rescues and cares for abandoned and injured cattle, offering shelter, nutritious feed, and veterinary treatment. Volunteers and donors help us maintain a safe home for cows while promoting humane animal welfare in the communities we serve.",
      stats: [
        { value: "500+", label: "Cows cared for" },
        { value: "1", label: "Goshala shelter" },
        { value: "12+", label: "Years of service" },
      ],
      features: [
        "Rescue and shelter",
        "Veterinary care",
        "Feed and fodder support",
        "Adoption coordination",
        "Community seva drives",
      ],
    },
    {
      title: "Challenges we address",
      icon: "⚠️",
      items: [
        { title: "Abandoned cattle", desc: "Injured and stray cows need immediate shelter and medical attention." },
        { title: "Feed costs", desc: "Rising fodder prices make daily care expensive for small goshalas." },
        { title: "Veterinary access", desc: "Specialist care is scarce in rural areas when animals fall ill." },
        { title: "Shelter capacity", desc: "Limited space and resources restrict how many animals can be rescued." },
      ],
    },
    {
      title: "Our approach",
      icon: "🎯",
      items: [
        { number: "01", title: "Rescue and intake", desc: "Coordinating with locals to bring injured and abandoned cows to safety." },
        { number: "02", title: "Daily care", desc: "Nutritious feed, clean shelter, and routine health checks for every animal." },
        { number: "03", title: "Veterinary partnerships", desc: "Working with vets for treatment, vaccination, and emergency care." },
        { number: "04", title: "Community engagement", desc: "Volunteer seva days and awareness on humane cow protection." },
      ],
    }
  ),
  p(
    "blood-donation",
    "Blood Donation",
    "Community blood donation camps, donor registration, and partnerships to ensure life-saving blood reaches those who need it.",
    ["Blood Donation Camps", "Donor Registration", "Blood Bank Support", "Awareness Campaigns"],
    {
      headline: "Saving Lives Through",
      accent: "Blood Donation",
      subtext:
        "Organizing voluntary donor camps and connecting communities with blood banks so patients never go without the care they need.",
    },
    {
      title: "Blood Donation Overview",
      icon: "🩸",
      description:
        "AJA Abhaya Hastham runs community blood donation camps across Hyderabad and Telangana, registering voluntary donors and coordinating with hospitals and blood banks. Our volunteers work to close the gap between willing donors and patients — with compassion, reliability, and respect for every unit donated.",
      stats: [
        { value: "1,000+", label: "Donors registered" },
        { value: "50+", label: "Camps held" },
        { value: "12+", label: "Partner hospitals" },
      ],
      features: [
        "Donor registration",
        "Camp organization",
        "Blood bank partnerships",
        "Awareness drives",
        "Emergency coordination",
      ],
    },
    {
      title: "Challenges we address",
      icon: "⚡",
      items: [
        { title: "Donor shortage", desc: "Many patients wait for blood because voluntary donors are hard to reach in time." },
        { title: "Myths and misconceptions", desc: "Fear and misinformation keep willing community members from donating." },
        { title: "Storage and logistics", desc: "Safe collection, storage, and timely delivery require strong local partnerships." },
        { title: "Emergency mobilization", desc: "Sudden demand during accidents or medical crises needs rapid donor outreach." },
      ],
    },
    {
      title: "Our approach",
      icon: "🎯",
      items: [
        { number: "01", title: "Community donor drives", desc: "Regular camps in neighbourhoods, colleges, and workplaces with trained medical staff." },
        { number: "02", title: "Donor registry", desc: "Maintaining a list of registered donors for faster matching when blood is urgently needed." },
        { number: "03", title: "Awareness sessions", desc: "Education to dispel myths and encourage safe, regular voluntary donation." },
        { number: "04", title: "Hospital partnerships", desc: "Direct coordination with blood banks and hospitals for collection and distribution." },
      ],
    }
  ),
  p(
    "blanket-destitution",
    "Blanket Distribution",
    "Supporting homeless individuals through shelter, winter relief, and rehabilitation programs.",
    ["Night Shelters", "Winter Relief", "Homeless Rehabilitation", "Basic Necessities"],
    {
      headline: "Warmth Through",
      accent: "Blanket Distribution",
      subtext: "Providing shelter, warmth, and dignity to those without a home.",
    },
    {
      title: "Blanket Distribution Overview",
      icon: "🛏️",
      description:
        "Our blanket distribution program provides immediate relief to homeless individuals through night shelters, winter blanket distribution, and pathways to rehabilitation and self-sufficiency.",
      stats: [
        { value: "500+", label: "People Sheltered" },
        { value: "2000+", label: "Blankets Distributed" },
        { value: "15+", label: "Shelter Partners" },
      ],
      features: [
        "Night Shelters",
        "Winter Relief",
        "Rehabilitation",
        "Food & Clothing",
        "Counseling Support",
      ],
    },
    {
      title: "Current Challenges",
      icon: "⚡",
      items: [
        { title: "Rising Homelessness", desc: "Growing numbers of people without stable shelter." },
        { title: "Winter Mortality", desc: "Cold weather poses severe health risks to homeless populations." },
        { title: "Mental Health", desc: "Many homeless individuals need psychological support." },
        { title: "Long-term Rehabilitation", desc: "Transitioning from shelter to sustainable livelihood." },
      ],
    },
    {
      title: "Strategic Approach",
      icon: "🎯",
      items: [
        { number: "01", title: "Emergency Relief", desc: "Rapid deployment of blankets during cold emergencies." },
        { number: "02", title: "Shelter Network", desc: "Partnering with NGOs for night shelter capacity." },
        { number: "03", title: "Rehabilitation", desc: "Skills training and job placement for sustainable exit." },
        { number: "04", title: "Community Awareness", desc: "Engaging volunteers and donors in winter relief response." },
      ],
    }
  ),
  p(
    "skill-development",
    "Skill Development",
    "Enhancing employability and practical skills for better career opportunities.",
    ["Skill Training", "Capacity Building", "Career Development"],
    { headline: "Building Skills for", accent: "Tomorrow", subtext: "Practical training for employability and career growth." },
    { title: "Skill Development Overview", icon: "🔨", description: "Skill development programs enhance employability through vocational training, apprenticeships, and industry-aligned curricula. We bridge the gap between education and employment.", stats: [{ value: "500+", label: "Trainees" }, { value: "20+", label: "Skill Courses" }, { value: "75%", label: "Placement Rate" }], features: ["Vocational Training", "Industry Partnerships", "Certification Programs", "Soft Skills", "Career Counseling"] },
    { title: "Current Challenges", icon: "⚡", items: [{ title: "Skill Mismatch", desc: "Education outcomes not aligned with industry needs." }, { title: "Youth Unemployment", desc: "High unemployment among educated youth." }, { title: "Informal Sector", desc: "Limited formal training for informal workers." }, { title: "Technology Change", desc: "Rapidly evolving skills requirements." }] },
    { title: "Strategic Approach", icon: "🎯", items: [{ number: "01", title: "Industry Alignment", desc: "Courses designed with employer input." }, { number: "02", title: "Hands-on Training", desc: "Practical workshops and apprenticeships." }, { number: "03", title: "Certification", desc: "Recognized credentials for job seekers." }, { number: "04", title: "Placement Support", desc: "Job fairs and employer connections." }] }
  ),
];

export function getProgramBySlug(slug: string): Program | undefined {
  return programs.find((p) => p.slug === slug);
}

export function getAllProgramSlugs(): string[] {
  return programs.map((p) => p.slug);
}

export function getProgramSummaries() {
  return programs.map(({ slug, title, shortDescription, features, image }) => ({
    slug,
    title,
    description: shortDescription,
    features,
    image,
  }));
}
